"use client";

import {
  CreditPackagePageVo,
  PlanPageVo,
  UserPlanVo,
  UserProfile,
} from "@/api";
import { AssetsExplorerModal } from "@/component/AssetsExplorerModal";
import { SpotlightProvider } from "@/component/NewUserGuide/SpotlightContext";
import { useMultipleConfigs } from "@/hooks/useConfig";
import { MultipleConfigsResult } from "@/service/config";
import { configStore } from "@/store/configStore";
import { pricingStore } from "@/store/pricing";
import { themeStore } from "@/store/theme";
import { userStore } from "@/store/user";
import { consoleCapturer } from "@/util/consoleCapturer";
import { AntdRegistry } from "@ant-design/nextjs-registry";
import { ProgressProvider } from "@bprogress/next/app";
import { App, ConfigProvider, theme } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";

interface ProvidersProps {
  children: React.ReactNode;
  initialUserData: {
    user: UserProfile | null;
    userPlanList: UserPlanVo[];
    userCredits: number;
    initialConfigs?: MultipleConfigsResult;
    planPage?: PlanPageVo | null;
    creditPackages?: CreditPackagePageVo | null;
  } | null;
}

const Providers = observer(({ children, initialUserData }: ProvidersProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  // 启用客户端配置自动刷新与同步
  useMultipleConfigs();

  // 在渲染期间立即同步水合数据（必须在首次渲染前完成）
  if (!isHydrated && initialUserData) {
    // 同步全局数据
    userStore.set("user", initialUserData.user);
    userStore.set("userPlanList", initialUserData.userPlanList);
    userStore.set("userCredits", initialUserData.userCredits);
    userStore.set("loading", false);

    // 同步全局配置数据
    const configs = initialUserData.initialConfigs;
    if (configs) {
      if (configs.tool_status_config) {
        configStore.setToolConfig(configs.tool_status_config);
      }
      if (configs["pricing_page_config"]) {
        configStore.setPricingPageConfig(configs["pricing_page_config"]);
      }
      if (configs["banner_config"]) {
        configStore.setBannerConfig(configs["banner_config"]);
      }
    }

    // 同步定价数据
    pricingStore.hydrate(
      initialUserData.planPage ?? { planList: [] },
      initialUserData.creditPackages ?? { creditPackageList: [] },
    );

    setIsHydrated(true);
  }

  // 初始化主题和日志抓取
  useEffect(() => {
    themeStore.init();
    consoleCapturer.init();
  }, []);

  // 在客户端运行时读取 CSS 变量值
  const isDark = themeStore.resolvedTheme === "dark";

  const primaryColor = useMemo(() => {
    if (typeof window === "undefined") {
      // SSR 时返回默认值
      return "#5251f6";
    }
    // 读取 CSS 变量值
    return (
      getComputedStyle(document.documentElement)
        .getPropertyValue("--color-primary")
        .trim() || "#5251f6"
    );
  }, []);

  return (
    <ProgressProvider
      color="var(--color-primary)"
      options={{ showSpinner: false }}
      shallowRouting
    >
      <AntdRegistry>
        <App>
          <ConfigProvider
            wave={{ disabled: true }}
            theme={{
              algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
              token: {
                colorPrimary: primaryColor,
                fontFamily: "var(--font-alibaba-pu-hui-ti)",
              },
              components: {
                Dropdown: {
                  paddingBlock: 12,
                  controlPaddingHorizontal: 16,
                  paddingXXS: 8,
                },
                Button: {
                  borderRadius: 99,
                  borderRadiusLG: 99,
                  borderRadiusSM: 99,
                  fontSizeLG: 14,
                  borderColorDisabled: "transparent",
                  colorBgContainerDisabled: "#CECEFA",
                  colorPrimaryHover: "#4E4DEA",
                  colorPrimaryActive: "#4241C5",
                  colorTextDisabled: "#FFFFFF",
                  defaultBorderColor: "#EAECF0",
                  textHoverBg: "rgba(0, 0, 0, 0.03)",
                  primaryShadow: "none",
                  controlHeightLG: 48,
                },
                Table: {
                  borderRadius: 12,
                  headerSplitColor: "none",
                  headerBg: "var(--color-background-elevated)",
                  headerColor: "var(--color-text-primary)",
                  colorBgContainer: "var(--color-background-card)",
                  borderColor: "var(--color-background-elevated)",
                  colorText: "var(--color-text-primary)",
                },
                Tabs: {
                  itemHoverColor: "var(--color-text-primary)",
                  itemActiveColor: "var(--color-text-primary)",
                  itemSelectedColor: "var(--color-text-primary)",
                  inkBarColor: "var(--color-text-primary)",
                  itemColor: "var(--color-text-primary)",
                },
                Modal: {
                  colorBgMask: "rgba(0, 0, 0, 0.85)",
                },
              },
            }}
          >
            <SpotlightProvider>
              {children}
              <AssetsExplorerModal />
            </SpotlightProvider>
          </ConfigProvider>
        </App>
      </AntdRegistry>
    </ProgressProvider>
  );
});

export default Providers;
